import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, GitMerge, Plus, Search, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, ErrorBanner, Loader, Money, PageHeader } from "@/components/common";
import { FINANCE, FRONT_OFFICE, MANAGEMENT } from "@/lib/i18n";
import { ClientForm } from "@/components/ClientForm";

export default function ClientsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const { data, loading, error, reload } = useFetch(() => api.get("/clients", { params: { q } }), [q]);
  const finance = FINANCE.includes(user.role);

  return (
    <div data-testid="clients-page">
      <PageHeader title="Clients" subtitle="Particuliers et entreprises, véhicules associés et solde" actions={
        <>
          {MANAGEMENT.includes(user.role) && <Button variant="outline" onClick={() => setDupOpen(true)} className="rounded-full" data-testid="clients-duplicates-button"><GitMerge className="mr-2 h-4 w-4" /> Doublons</Button>}
          {FRONT_OFFICE.includes(user.role) && <><Button variant="outline" onClick={() => setOpen("entreprise")} className="rounded-full" data-testid="clients-new-company-button"><Building2 className="mr-2 h-4 w-4" /> Client professionnel</Button><Button onClick={() => setOpen("particulier")} className="rounded-full" data-testid="clients-new-button"><Plus className="mr-2 h-4 w-4" /> Nouveau client</Button></>}
        </>
      } />
      <div className="relative mb-4 max-w-md"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, téléphone, email, TVA…" className="h-11 rounded-full pl-10" data-testid="clients-search-input" /></div>
      {loading && !data ? <Loader /> : error ? <ErrorBanner message={error} onRetry={reload} /> : !data.length ? <EmptyState title="Aucun client" /> : (
        <div className="surface overflow-x-auto rise">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left"><th className="p-3 eyebrow">Client</th><th className="p-3 eyebrow">Contact</th><th className="p-3 eyebrow">Véhicules</th>{finance && <th className="p-3 text-right eyebrow">Solde dû</th>}</tr></thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/clients/${c.id}`)} className="cursor-pointer border-b border-border/50 transition-colors hover:bg-muted/50 last:border-0" data-testid={`client-row-${c.id}`}>
                  <td className="p-3"><div className="flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-full bg-secondary">{c.type === "entreprise" ? <Building2 className="h-4 w-4" /> : <User className="h-4 w-4" />}</span><div><div className="font-semibold">{c.display_name}</div><div className="text-xs text-muted-foreground">{c.type === "entreprise" ? c.vat_number || "Entreprise" : "Particulier"}{c.address?.city ? ` · ${c.address.city}` : ""}</div></div></div></td>
                  <td className="p-3"><div>{c.phone}</div><div className="text-xs text-muted-foreground">{c.email}</div></td>
                  <td className="p-3 tabular">{c.vehicles_count}</td>
                  {finance && <td className={`p-3 text-right font-semibold tabular ${c.balance > 0 ? "text-amber-500" : "text-muted-foreground"}`}><Money value={c.balance} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}><DialogContent className="max-w-2xl" aria-describedby={undefined} data-testid="client-dialog"><DialogHeader><DialogTitle>{open === "entreprise" ? "Nouveau client professionnel" : "Nouveau client"}</DialogTitle></DialogHeader>{open && <ClientForm initial={{ type: open }} onSaved={(c) => { setOpen(null); reload(); navigate(`/clients/${c.id}`); }} />}</DialogContent></Dialog>
      <DuplicatesDialog open={dupOpen} onOpenChange={setDupOpen} onMerged={reload} />
    </div>
  );
}

function DuplicatesDialog({ open, onOpenChange, onMerged }) {
  const { data, loading, reload } = useFetch(() => api.get("/clients/duplicates"), [open], { enabled: open });
  const merge = async (target, source) => {
    if (!window.confirm(`Fusionner « ${source.display_name} » dans « ${target.display_name} » ? L'historique sera conservé.`)) return;
    try { await api.post(`/clients/${target.id}/merge`, { source_id: source.id }); toast.success("Fusion effectuée, historique conservé"); reload(); onMerged(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="duplicates-dialog">
        <DialogHeader><DialogTitle>Doublons potentiels</DialogTitle></DialogHeader>
        {loading ? <Loader /> : !data?.length ? <EmptyState title="Aucun doublon détecté" /> : data.map((g, i) => (
          <div key={i} className="rounded-xl border border-border p-3 text-sm" data-testid={`duplicate-group-${i}`}>
            <div className="mb-2 font-semibold text-amber-500">{g.reason}</div>
            {g.clients.map((c) => (
              <div key={c.id} className="flex items-center justify-between border-b border-border/50 py-2 last:border-0">
                <div><div className="font-medium">{c.display_name}</div><div className="text-xs text-muted-foreground">{c.phone} · {c.email}</div></div>
                <div className="flex gap-1">{g.clients.filter((o) => o.id !== c.id).map((o) => <Button key={o.id} size="sm" variant="outline" onClick={() => merge(c, o)} data-testid={`merge-into-${c.id}`}>Conserver celui-ci</Button>)}</div>
              </div>
            ))}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
